/**
 * Resolves trading-rules dashboard mock mode (when `?v=` is present).
 *
 * - `?v=…` — mock; empty `?v` or `?v=` uses `2` (warning).
 * - `?live=1` — force real API (overrides mock).
 *
 * Day-banner demos (`?today=`):
 * - clean | ontrack — no issues
 * - violations — rules broken today
 * - journal — missing journals
 * - trades — trade count exceeded
 * - loss — daily loss breached
 * - lockout — trading locked
 * - profitgave | gaveback — hit daily profit then fell below (soft note)
 * - extreme — loss + trades + violations
 *
 * When `?today=` is omitted, `?v=1|2|3` still drives mock API data (and the banner).
 */
export function resolveTradingRulesMockScenario() {
  const urlParams = new URLSearchParams(window.location.search);

  if (urlParams.get('live') === '1' || urlParams.get('live') === 'true') {
    return { useMock: false, scenarioVersion: null };
  }

  const q = urlParams.get('v');
  if (q !== null) {
    return { useMock: true, scenarioVersion: q || '2' };
  }

  // Allow ?today= demos without forcing full mock metrics (banner-only), unless v is set.
  return { useMock: false, scenarioVersion: null };
}

const TODAY_DEMO_ALIASES = {
  clean: 'clean',
  ontrack: 'clean',
  ok: 'clean',
  violations: 'violations',
  broken: 'violations',
  journal: 'journal',
  journals: 'journal',
  trades: 'trades',
  overtrade: 'trades',
  loss: 'loss',
  risk: 'loss',
  lockout: 'lockout',
  locked: 'lockout',
  profitgave: 'profitgave',
  gaveback: 'profitgave',
  profittarget: 'profitgave',
  extreme: 'extreme',
};

/**
 * Demo overrides for the Health "Today" strip.
 * Prefer `?today=…`; fall back to mapping from `?v=` when present.
 */
export function resolveTodayDemoScenario(searchParams) {
  const urlParams = searchParams instanceof URLSearchParams
    ? searchParams
    : new URLSearchParams(window.location.search);
  if (urlParams.get('live') === '1' || urlParams.get('live') === 'true') {
    return null;
  }

  const rawToday = (urlParams.get('today') || '').trim().toLowerCase();
  if (rawToday && TODAY_DEMO_ALIASES[rawToday]) {
    return TODAY_DEMO_ALIASES[rawToday];
  }

  const v = urlParams.get('v');
  if (v === null) return null;
  if (v === '1' || v === 'healthy') return 'clean';
  if (v === '2' || v === 'warning' || v === '') return 'violations';
  if (v === '3' || v === 'extreme') return 'extreme';
  return null;
}

/** Shape overrides applied on top of live/mock ConstraintBand inputs. */
export function getTodayDemoOverrides(scenario) {
  if (!scenario) return null;

  const base = {
    isTradingLocked: false,
    lockoutEndsText: null,
    todayChange: 120,
    todayChangePercent: 0.15,
    tradesTaken: 2,
    maxTrades: 8,
    dailyLossLimit: -500,
    dailyLossBreached: false,
    journalsCreated: 2,
    positionsRequiringJournal: 2,
    todayWinRate: 0.5,
    todayWinningTrades: 1,
    todayTotalTrades: 2,
    todayViolations: [],
  };

  switch (scenario) {
    case 'clean':
      return base;
    case 'violations':
      return {
        ...base,
        todayChange: -85,
        todayChangePercent: -0.12,
        todayViolations: [
          { rule_type: 'max_trades_per_day', id: 'demo-1' },
          { rule_type: 'position_loss_exceeded', id: 'demo-2' },
          { rule_type: 'position_loss_exceeded', id: 'demo-3' },
          { rule_type: 'stop_loss_modified', id: 'demo-4' },
          { rule_type: 'profit_target_modified', id: 'demo-5' },
        ],
        tradesTaken: 6,
        journalsCreated: 4,
        positionsRequiringJournal: 4,
      };
    case 'journal':
      return {
        ...base,
        todayViolations: [],
        journalsCreated: 0,
        positionsRequiringJournal: 3,
        tradesTaken: 3,
      };
    case 'trades':
      return {
        ...base,
        tradesTaken: 9,
        maxTrades: 5,
        todayViolations: [
          { rule_type: 'max_trades_per_day', id: 'demo-t1' },
          { rule_type: 'max_trades_per_day', id: 'demo-t2' },
        ],
      };
    case 'loss':
      return {
        ...base,
        todayChange: -620,
        todayChangePercent: -1.1,
        dailyLossLimit: -500,
        dailyLossBreached: true,
        todayViolations: [
          { rule_type: 'daily_loss_exceeded', id: 'demo-l1' },
        ],
      };
    case 'lockout':
      return {
        ...base,
        isTradingLocked: true,
        lockoutEndsText: '4:00 PM today',
        todayChange: -620,
        todayChangePercent: -1.1,
        dailyLossBreached: true,
        dailyLossLimit: -500,
        todayViolations: [
          { rule_type: 'daily_loss_exceeded', id: 'demo-lock' },
        ],
      };
    case 'profitgave':
      return {
        ...base,
        todayChange: 130,
        todayChangePercent: 0.08,
        todayViolations: [
          {
            rule_type: 'profit_target_per_day',
            id: 'demo-pt-gave',
            threshold_value: 500,
            actual_value: 130,
            violation_metadata: {
              currentlyAboveTarget: false,
              peakProfit: 510.98,
            },
          },
        ],
      };
    case 'extreme':
      return {
        ...base,
        todayChange: -856,
        todayChangePercent: -2.4,
        dailyLossLimit: -500,
        dailyLossBreached: true,
        tradesTaken: 12,
        maxTrades: 8,
        journalsCreated: 2,
        positionsRequiringJournal: 8,
        todayViolations: [
          { rule_type: 'daily_loss_exceeded', id: 'demo-e1' },
          { rule_type: 'max_trades_per_day', id: 'demo-e2' },
          { rule_type: 'position_loss_exceeded', id: 'demo-e3' },
          { rule_type: 'stop_loss_modified', id: 'demo-e4' },
          { rule_type: 'profit_target_modified', id: 'demo-e5' },
        ],
      };
    default:
      return null;
  }
}
